import { CircularProgress } from '@mui/material';
import moment from 'moment';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import {
  Payment,
  StudyIssuanceRequest,
} from '../../../../shared/api/hscan/hospital';
import { useCaseByIds } from '../../../../shared/hooks/caseHook';
import { useIssuancesInPayment } from '../../../../shared/hooks/hospitalHook';
import expandDown from '../../assets/icons/icon_expand-down.svg';
import expandRight from '../../assets/icons/icon_expand-right.svg';
import { IconButton } from '../../common/components/Icon';
import {
  MobilePaginationWithoutRowsPerPage,
  MobileRowsPerPage,
  MobileTable,
  MobileTableFullRow,
  MobileTableRow,
  MobileTableSubFullRow,
  MobileTableWrapper,
  RowContainer,
} from '../../common/components/MobileTable';
import { TableAndElementInformation } from '../../common/components/SortableTable/PresentationalTable';
import { useModalityLabel } from '../../hooks/modalityHook';
import { krwCurrencyFormatter as formatter } from '../../utils';

import { CdDeliveryPaymentTableProps } from './CasesTable';
import {
  IconButtonWrapper,
  PaidStatusMessage,
  PayFailedStatusMessage,
  TableContext,
  TableElementContext,
  TableElementInformationProvider,
  usePaymentMethodString,
} from './PaymentTable';
import { ReceiptMessage } from './ReceiptMessage';
import { StatusMessage } from './StatusMessage';
import { StudyIssuanceRequestsTableProps } from './StudyIssuanceRequestsTable';
import { TrackingNumberMessage } from './TrackingNumberMessage';

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
`;

interface MobilePaymentTableElementProps
  extends TableAndElementInformation<Payment, 'id'> {
  onClickReceipt?: () => void;
  onClickStatus?: (request: StudyIssuanceRequest) => void;
}
export const MobilePaymentTableElement = (
  props: MobilePaymentTableElementProps,
) => {
  const { element: payment, onClickStatus } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.payments.table',
  });
  const [showDetail, setShowDetail] = useState(false);

  const paymentMethodString = usePaymentMethodString();

  const paymentPrice = useMemo(() => {
    if (payment.type === 'SIMPLE') {
      return t('priceField', { price: formatter(payment.price.amount) });
    }
    if (payment.type === 'CD_DELIVERY') {
      return t('priceField', { price: formatter(payment.price.amount) });
    }
    if (payment.type === 'ADMIN_STUDIES_ONLINE') {
      return t('priceField', { price: formatter(payment.price.amount) });
    }
    if (payment.price.totalFee === payment.price.refundableFee) {
      // 환불된 적 없음
      return t('priceField', {
        price: formatter(payment.price.totalFee),
      });
    }
    return (
      // 일부환불 또는 전액환불
      <>
        <span
          style={{
            textDecoration: 'line-through',
            opacity: '0.5',
          }}>
          {t('priceField', {
            price: formatter(payment.price.totalFee),
          })}
        </span>{' '}
        {t('priceField', {
          price: formatter(payment.price.refundableFee),
        })}
      </>
    );
  }, [payment, t]);

  return (
    <MobileTable>
      <tbody>
        <MobileTableRow label={`${t('header.id')}`}>
          {payment.id ?? ''}
        </MobileTableRow>
        <MobileTableRow label={`${t('header.paidAt')}`}>
          {payment.paidAt?.length
            ? moment(payment.paidAt).format('YYYY.MM.DD HH:mm:ss')
            : ''}
        </MobileTableRow>
        <MobileTableRow label={`${t('header.paymentMethod')}`}>
          {payment.paymentMethod
            ? paymentMethodString[payment.paymentMethod]
            : ''}
        </MobileTableRow>
        <MobileTableRow label={`${t('header.price')}`}>
          {paymentPrice}
        </MobileTableRow>
        <MobileTableRow label={`${t('header.status')}`}>
          {payment.paymentStatus === 'PAID' ? (
            <PaidStatusMessage>{`${t(
              'paymentStatus.paid',
            )}`}</PaidStatusMessage>
          ) : payment.paymentStatus === 'PARTIAL_REFUND' ? (
            <PayFailedStatusMessage>{`${t(
              'paymentStatus.partialRefund',
            )}`}</PayFailedStatusMessage>
          ) : (
            <PayFailedStatusMessage>{`${t(
              'paymentStatus.allRefund',
            )}`}</PayFailedStatusMessage>
          )}
        </MobileTableRow>
        <MobileTableRow label={`${t('header.receipt')}`}>
          <ReceiptMessage receiptUrl={payment.receiptUrl} />
        </MobileTableRow>
        <MobileTableRow label={`${t('header.trackingNumber')}`}>
          {(payment.type === 'ISSUE' || payment.type === 'CD_DELIVERY') && (
            <TrackingNumberMessage payment={payment} />
          )}
        </MobileTableRow>
        {(payment.type === 'ISSUE' || payment.type === 'CD_DELIVERY') && (
          <MobileTableFullRow
            onClick={() => setShowDetail(show => !show)}
            label={
              showDetail ? (
                <ButtonContent>
                  {t('mobile.fold')}
                  <IconButtonWrapper>
                    <IconButton
                      imageUrl={expandDown}
                      width={'14px'}
                      height={'8px'}
                      color="#666973"
                    />
                  </IconButtonWrapper>
                </ButtonContent>
              ) : (
                <ButtonContent>
                  {t('mobile.expand')}
                  <IconButtonWrapper>
                    <IconButton
                      imageUrl={expandRight}
                      width={'8px'}
                      height={'14px'}
                      color="#666973"
                    />
                  </IconButtonWrapper>
                </ButtonContent>
              )
            }
          />
        )}
        {showDetail &&
          (payment.type === 'ISSUE' ? (
            <MobileStudyIssuanceRequestsTable
              payment={payment}
              onClickStatus={onClickStatus}
            />
          ) : payment.type === 'CD_DELIVERY' ? (
            <MobileCasesTable payment={payment} />
          ) : null)}
      </tbody>
    </MobileTable>
  );
};

const MobileStudyIssuanceRequestsTable = ({
  payment,
  onClickStatus,
}: StudyIssuanceRequestsTableProps) => {
  const { data: issuanceRequests, isFetching } = useIssuancesInPayment({
    paymentId: payment.id,
  });

  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.payments.table.requestTable',
  });
  const modalityLabel = useModalityLabel();

  return issuanceRequests ? (
    <>
      {issuanceRequests.map((issuance, index) => (
        <Fragment key={issuance.id}>
          <MobileTableSubFullRow label={`${index + 1}.`} />
          <MobileTableRow label={t('studyDate')}>
            {new Date(issuance.studyDate).toLocaleDateString()}
          </MobileTableRow>
          <MobileTableRow label={t('hospitalName')}>
            {issuance.hospitalName}
          </MobileTableRow>
          <MobileTableRow label={t('modalities')}>
            {issuance.modalities
              .map(modality => modalityLabel(modality))
              .join(', ')}
          </MobileTableRow>
          <MobileTableRow label={t('studyDescription')}>
            {issuance.studyDescription}
          </MobileTableRow>
          <MobileTableRow label={t('receiveMethod')}>
            {issuance.mailingIncluded ? t('mailingIncluded') : t('onlineOnly')}
          </MobileTableRow>
          <MobileTableRow label={t('status')}>
            <StatusMessage
              onClick={() => onClickStatus?.(issuance)}
              status={issuance.status}
            />
          </MobileTableRow>
        </Fragment>
      ))}
    </>
  ) : isFetching ? (
    <RowContainer>
      <td align="center" colSpan={2}>
        <CircularProgress />
      </td>
    </RowContainer>
  ) : (
    <>error</>
  );
};

const MobileCasesTable = ({ payment }: CdDeliveryPaymentTableProps) => {
  const { data: cases, isFetching } = useCaseByIds({ ids: payment.caseIds });

  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.payments.table.requestTable',
  });
  const modalityLabel = useModalityLabel();

  return cases ? (
    <>
      {cases.map((c, index) => (
        <Fragment key={c.caseId}>
          <MobileTableSubFullRow label={`${index + 1}.`} />
          <MobileTableRow label={t('studyDate')}>
            {moment(c.studyDate).format('YYYY.MM.DD')}
          </MobileTableRow>
          <MobileTableRow label={t('hospitalName')}>
            {c.institutionName}
          </MobileTableRow>
          <MobileTableRow label={t('modalities')}>
            {modalityLabel(c.modality)}
          </MobileTableRow>
          <MobileTableRow label={t('studyDescription')}>
            {c.studyDescription}
          </MobileTableRow>
        </Fragment>
      ))}
    </>
  ) : isFetching ? (
    <RowContainer>
      <td align="center" colSpan={2}>
        <CircularProgress />
      </td>
    </RowContainer>
  ) : (
    <>error</>
  );
};

interface MobilePaymentTableProps {
  onClickStatus?: (request: StudyIssuanceRequest) => void;
}
export const MobilePaymentTable = ({
  onClickStatus,
}: MobilePaymentTableProps) => {
  const Element = useCallback(
    (props: TableAndElementInformation<Payment, 'id'>) => (
      <MobilePaymentTableElement {...props} onClickStatus={onClickStatus} />
    ),
    [onClickStatus],
  );
  return (
    <MobileTableWrapper
      tableContext={TableContext}
      elementContext={TableElementContext}
      ElementProvider={TableElementInformationProvider}
      Element={Element}
      Header={MobileRowsPerPage}
      Footer={MobilePaginationWithoutRowsPerPage}
    />
  );
};
